import React, { useEffect, useMemo, useState } from 'react';
import AuthorizationHoldFlagService from './AuthorizationHoldFlagService';
import './AuthorizationHoldFlagManager.css';

const flagService = new AuthorizationHoldFlagService();

const COLOR_OPTIONS = [
  { value: 'none', label: 'No Color' },
  { value: 'red', label: 'Red' },
  { value: 'green', label: 'Green' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'blue', label: 'Blue' },
  { value: 'purple', label: 'Purple' },
  { value: 'gray', label: 'Gray' },
];

const emptyForm = {
  flagKey: '',
  label: '',
  description: '',
  color: 'none',
  active: true,
  systemFlag: false,
};

const AuthorizationHoldFlagManager = () => {
  const [flags, setFlags] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingFlagId, setEditingFlagId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const role = localStorage.getItem('role')?.replace('ROLE_', '') || '';
  const canManage = role === 'ADMIN' || role === 'MANAGER';

  useEffect(() => {
    loadFlags();
  }, []);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await flagService.getAllDefinitions();

      setFlags(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load authorization hold flags:', err);
      setError(err.message || 'Failed to load authorization hold flags.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingFlagId(null);
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const startEdit = (flag) => {
    setEditingFlagId(flag.flagId);

    setForm({
      flagKey: flag.flagKey || '',
      label: flag.label || '',
      description: flag.description || '',
      color: flag.color || 'none',
      active: flag.active !== false,
      systemFlag: flag.systemFlag === true,
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveFlag = async () => {
    if (!canManage) {
      alert('Only admin and manager can manage flags.');
      return;
    }

    if (!form.label.trim()) {
      alert('Flag name is required.');
      return;
    }

    const payload = {
      flagKey: form.flagKey.trim(),
      label: form.label.trim(),
      description: form.description.trim(),
      color: form.color || 'none',
      active: form.active,
      systemFlag: form.systemFlag,
    };

    try {
      setSaving(true);
      setError('');

      if (editingFlagId) {
        await flagService.updateDefinition(editingFlagId, payload);
        showSuccess('Flag updated.');
      } else {
        await flagService.createDefinition(payload);
        showSuccess('Flag created.');
      }

      resetForm();
      await loadFlags();
    } catch (err) {
      console.error('Failed to save flag:', err);
      alert(err.message || 'Failed to save flag.');
    } finally {
      setSaving(false);
    }
  };

  const deleteFlag = async (flag) => {
    if (!canManage) {
      alert('Only admin and manager can delete flags.');
      return;
    }

    if (flag.systemFlag) {
      alert('System flags cannot be deleted. You can deactivate it instead.');
      return;
    }

    const confirmed = window.confirm(
      `Delete "${flag.label}"? This will remove it from any holds where it was selected.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await flagService.deleteDefinition(flag.flagId);
      showSuccess('Flag deleted.');
      await loadFlags();

      if (editingFlagId === flag.flagId) {
        resetForm();
      }
    } catch (err) {
      alert(err.message || 'Failed to delete flag.');
    }
  };

  const toggleActive = async (flag) => {
    const payload = {
      flagKey: flag.flagKey,
      label: flag.label,
      description: flag.description || '',
      color: flag.color || 'none',
      active: !flag.active,
      systemFlag: flag.systemFlag === true,
    };

    try {
      await flagService.updateDefinition(flag.flagId, payload);
      showSuccess(flag.active ? 'Flag deactivated.' : 'Flag activated.');
      await loadFlags();
    } catch (err) {
      alert(err.message || 'Failed to update flag.');
    }
  };

  const filteredFlags = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return flags.filter((flag) => {
      if (!showInactive && flag.active === false) {
        return false;
      }

      if (!term) {
        return true;
      }

      return (
        String(flag.label || '').toLowerCase().includes(term) ||
        String(flag.flagKey || '').toLowerCase().includes(term) ||
        String(flag.description || '').toLowerCase().includes(term) ||
        String(flag.color || '').toLowerCase().includes(term)
      );
    });
  }, [flags, searchTerm, showInactive]);

  const getColorLabel = (value) => {
    return (
      COLOR_OPTIONS.find((option) => option.value === value)?.label ||
      'No Color'
    );
  };

  return (
    <div className="auth-flag-manager-page">
      <div className="auth-flag-manager-header">
        <div>
          <h1>Authorization Hold Flags</h1>
          <p>
            Add, edit, delete, activate, and color-code flags used on
            authorization holds.
          </p>
        </div>

        <button type="button" onClick={loadFlags} className="flag-refresh-btn">
          Refresh
        </button>
      </div>

      {error && <div className="flag-error">{error}</div>}
      {success && <div className="flag-success">{success}</div>}

      {!canManage && (
        <div className="flag-error">
          Only ADMIN and MANAGER can manage authorization hold flags.
        </div>
      )}

      <div className="flag-layout">
        <section className="flag-form-card">
          <h2>{editingFlagId ? 'Edit Flag' : 'Create Flag'}</h2>

          <label>
            Flag Name
            <input
              type="text"
              value={form.label}
              onChange={(e) => updateForm('label', e.target.value)}
              placeholder="Example: Needs Referral"
              disabled={!canManage}
            />
          </label>

          <label>
            Backend Key
            <input
              type="text"
              value={form.flagKey}
              onChange={(e) => updateForm('flagKey', e.target.value)}
              placeholder="Example: NEEDS_REFERRAL"
              disabled={!canManage || !!editingFlagId}
            />
          </label>

          <p className="flag-help-text">
            Backend key can be blank when creating a normal flag. The backend
            will create one from the name. Once created, it should not be
            changed.
          </p>

          <label>
            Description
            <textarea
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              placeholder="Explain what this flag means."
              disabled={!canManage}
            />
          </label>

          <label>
            Color
            <select
              value={form.color}
              onChange={(e) => updateForm('color', e.target.value)}
              disabled={!canManage}
            >
              {COLOR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="flag-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => updateForm('active', e.target.checked)}
                disabled={!canManage}
              />
              Active
            </label>

            <label>
              <input
                type="checkbox"
                checked={form.systemFlag}
                onChange={(e) => updateForm('systemFlag', e.target.checked)}
                disabled={!canManage || !!editingFlagId}
              />
              System Flag
            </label>
          </div>

          <div className="flag-form-actions">
            <button
              type="button"
              onClick={saveFlag}
              disabled={!canManage || saving}
              className="flag-save-btn"
            >
              {saving
                ? 'Saving...'
                : editingFlagId
                ? 'Save Changes'
                : 'Create Flag'}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="flag-secondary-btn"
            >
              Clear
            </button>
          </div>
        </section>

        <section className="flag-list-card">
          <div className="flag-list-toolbar">
            <label>
              Search Flags
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search name, key, color..."
              />
            </label>

            <label className="flag-show-inactive">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive
            </label>
          </div>

          {loading ? (
            <p className="flag-loading">Loading flags...</p>
          ) : filteredFlags.length === 0 ? (
            <div className="flag-empty">No flags found.</div>
          ) : (
            <div className="flag-table-wrapper">
              <table className="flag-table">
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th>Color</th>
                    <th>Status</th>
                    <th>System</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredFlags.map((flag) => (
                    <tr
                      key={flag.flagId}
                      className={flag.active === false ? 'inactive-row' : ''}
                    >
                      <td>
                        <div className="flag-name-cell">
                          <span
                            className={`flag-preview-chip flag-color-${
                              flag.color || 'none'
                            }`}
                          >
                            {flag.label || 'Unnamed Flag'}
                          </span>

                          <strong>{flag.flagKey}</strong>

                          <p>{flag.description || 'No description.'}</p>
                        </div>
                      </td>

                      <td>{getColorLabel(flag.color)}</td>

                      <td>
                        <span
                          className={
                            flag.active
                              ? 'flag-status active'
                              : 'flag-status inactive'
                          }
                        >
                          {flag.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td>{flag.systemFlag ? 'Yes' : 'No'}</td>

                      <td>
                        <div className="flag-row-actions">
                          <button type="button" onClick={() => startEdit(flag)}>
                            Edit
                          </button>

                          <button
                            type="button"
                            className="flag-secondary-btn small"
                            onClick={() => toggleActive(flag)}
                            disabled={!canManage}
                          >
                            {flag.active ? 'Deactivate' : 'Activate'}
                          </button>

                          <button
                            type="button"
                            className="flag-delete-btn"
                            onClick={() => deleteFlag(flag)}
                            disabled={!canManage || flag.systemFlag}
                            title={
                              flag.systemFlag
                                ? 'System flags cannot be deleted.'
                                : 'Delete flag'
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AuthorizationHoldFlagManager;